import React, { useState } from "react";
import {
  Alert,
  Button,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { workOrdersApi } from "../../lib/apiSubcontractors";
import type { WorkOrder, WorkOrderStatus } from "../../types/subcontractors";
import { WorkOrderForm } from "./WorkOrderForm";
import { ResponsiveTable } from "../common/ResponsiveTable";
import { SavedBillingViews } from "../billing/BillingWorkflowTools";
import { BillingRecordPicker } from "../billing/BillingRecordPicker";
export const WorkOrderList: React.FC<{
  jobId?: number;
  subcontractorId?: number;
  showCreateButton?: boolean;
}> = ({ jobId, subcontractorId, showCreateButton = true }) => {
  const navigate = useNavigate(),
    client = useQueryClient(),
    [params, setParams] = useSearchParams(),
    [creating, setCreating] = useState(false);
  const page = Math.max(1, Number(params.get("wo_page")) || 1);
  const filters = {
    job: jobId || Number(params.get("wo_job")) || undefined,
    subcontractor:
      subcontractorId || Number(params.get("wo_vendor")) || undefined,
    status: (params.get("wo_status") as WorkOrderStatus) || undefined,
    search: params.get("wo_search") || undefined,
    overdue: params.get("wo_overdue") === "true" || undefined,
    start_date_after: params.get("wo_from") || undefined,
    start_date_before: params.get("wo_to") || undefined,
  };
  const list = useQuery({
    queryKey: ["work-orders", filters, page],
    queryFn: () => workOrdersApi.list(filters, page),
  });
  const change = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    if (key !== "wo_page") next.delete("wo_page");
    setParams(next);
  };
  const queue = (status: string, overdue = false) => {
    const next = new URLSearchParams(params);
    next.delete("wo_page");
    next.delete("wo_status");
    next.delete("wo_overdue");
    if (status) next.set("wo_status", status);
    if (overdue) next.set("wo_overdue", "true");
    setParams(next);
  };
  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack
        direction="row"
        sx={{ gap: 2, flexWrap: "wrap", justifyContent: "space-between" }}
      >
        <Typography level="h3">Work orders</Typography>
        {showCreateButton && (
          <Button onClick={() => setCreating(true)}>Create work order</Button>
        )}
      </Stack>
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
        {[
          ["Unsent drafts", "draft"],
          ["Awaiting acceptance", "sent"],
          ["Awaiting invoicing", "completed"],
        ].map(([label, status]) => (
          <Button key={status} variant="soft" onClick={() => queue(status)}>
            {label}
          </Button>
        ))}
        <Button variant="soft" color="warning" onClick={() => queue("", true)}>
          Overdue
        </Button>
        <Button variant="plain" onClick={() => queue("")}>
          All statuses
        </Button>
      </Stack>
      <SavedBillingViews kind="work-orders" />
      <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
        <FormControl>
          <FormLabel>Search orders</FormLabel>
          <Input
            value={filters.search || ""}
            onChange={(e) => change("wo_search", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Status</FormLabel>
          <Select
            value={filters.status || ""}
            onChange={(_, v) => change("wo_status", v || "")}
          >
            <Option value="">All</Option>
            {[
              "draft",
              "sent",
              "accepted",
              "in_progress",
              "completed",
              "invoiced",
              "paid",
              "rejected",
              "cancelled",
            ].map((s) => (
              <Option key={s} value={s}>
                {s.replaceAll("_", " ")}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Start from</FormLabel>
          <Input
            type="date"
            value={filters.start_date_after || ""}
            onChange={(e) => change("wo_from", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Start through</FormLabel>
          <Input
            type="date"
            value={filters.start_date_before || ""}
            onChange={(e) => change("wo_to", e.target.value)}
          />
        </FormControl>
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {!jobId && (
          <BillingRecordPicker
            label="Job filter"
            endpoint="/api/jobs/"
            value={filters.job}
            onChange={(row) => change("wo_job", String(row?.id || ""))}
          />
        )}{" "}
        {!subcontractorId && (
          <BillingRecordPicker
            label="Subcontractor filter"
            endpoint="/api/subcontractors/"
            value={filters.subcontractor}
            onChange={(row) => change("wo_vendor", String(row?.id || ""))}
          />
        )}
      </Stack>
      {list.isError && (
        <Alert color="danger">
          Unable to load orders.{" "}
          <Button onClick={() => list.refetch()}>Retry</Button>
        </Alert>
      )}
      {list.isLoading ? (
        <Typography>Loading orders…</Typography>
      ) : (
        <ResponsiveTable<WorkOrder>
          rows={list.data?.results || []}
          getRowKey={(r) => r.id}
          pagination={{
            page,
            pageSize: 20,
            totalCount: list.data?.count || 0,
            onPageChange: (p) => change("wo_page", String(p)),
            itemNoun: "work orders",
          }}
          columns={[
            {
              key: "title",
              header: "Order",
              mobileVariant: "header",
              render: (r) => (
                <Button
                  variant="plain"
                  onClick={() => navigate(`/operations/work-orders/${r.id}`)}
                >
                  {r.work_order_number} · {r.title}
                </Button>
              ),
            },
            {
              key: "job",
              header: "Job / site",
              render: (r) => (
                <Typography>
                  {r.job_number} · {r.job_address}
                </Typography>
              ),
            },
            {
              key: "vendor",
              header: "Subcontractor",
              render: (r) => <Typography>{r.subcontractor_name}</Typography>,
            },
            {
              key: "dates",
              header: "Schedule",
              render: (r) => (
                <Typography>
                  {r.start_date} – {r.end_date}
                  {r.is_overdue ? " · Overdue" : ""}
                </Typography>
              ),
            },
            {
              key: "amount",
              header: "Total",
              render: (r) => (
                <Typography>
                  ${Number(r.total_cost || r.agreed_cost || 0).toFixed(2)}
                </Typography>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (r) => <Chip>{r.status.replaceAll("_", " ")}</Chip>,
            },
          ]}
          rowActions={(r) => (
            <Button
              size="sm"
              variant="outlined"
              onClick={() => navigate(`/operations/work-orders/${r.id}`)}
            >
              Open order
            </Button>
          )}
        />
      )}
      <Modal open={creating} onClose={() => setCreating(false)}>
        <ModalDialog
          sx={{
            width: "min(900px,95vw)",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <ModalClose />
          <Typography level="h4">Create work order</Typography>
          <WorkOrderForm
            jobId={jobId}
            subcontractorId={subcontractorId}
            onCancel={() => setCreating(false)}
            onSuccess={(record) => {
              setCreating(false);
              client.invalidateQueries({ queryKey: ["work-orders"] });
              if (record?.id) navigate(`/operations/work-orders/${record.id}`);
            }}
          />
        </ModalDialog>
      </Modal>
    </Stack>
  );
};
